import { useModel } from "@umijs/max";
import { App, ConfigProvider, Empty } from "antd";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ANTD_ICON_CONFIG } from "../../utils/antdIconConfig";
import { TOAST_MESSAGE_CONFIG, ToastBridge } from "../../utils/toast";
import { ThemeScope } from "./ThemeScope";

export { ThemeScope } from "./ThemeScope";

export type ThemeMode = "light" | "dark";
export type ThemePreference = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "nomilo_theme_mode";
const SYSTEM_DARK_MQ = "(prefers-color-scheme: dark)";

interface ThemeContextValue {
  mode: ThemeMode;
  preference: ThemePreference;
  setPreference: (pref: ThemePreference) => void;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

function readInitialPreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  const v = localStorage.getItem(THEME_STORAGE_KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

function getSystemMode(): ThemeMode {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia(SYSTEM_DARK_MQ).matches ? "dark" : "light";
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: "dark",
  preference: "system",
  setPreference: () => {},
  setMode: () => {},
  toggle: () => {},
});

const renderUnifiedEmpty = () => (
  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" />
);

export function useThemeMode() {
  return useContext(ThemeContext);
}

const ThemeRoot: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    readInitialPreference
  );
  const [systemMode, setSystemMode] = useState<ThemeMode>(getSystemMode);
  const initialStateModel = useModel("@@initialState");

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(SYSTEM_DARK_MQ);
    const handler = (e: MediaQueryListEvent) =>
      setSystemMode(e.matches ? "dark" : "light");
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  const mode: ThemeMode = preference === "system" ? systemMode : preference;

  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(THEME_STORAGE_KEY, preference);
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
  }, [preference, mode]);

  const lastWrittenModeRef = useRef<ThemeMode | null>(null);
  useEffect(() => {
    if (!initialStateModel?.setInitialState) return;
    if (lastWrittenModeRef.current === mode) return;
    lastWrittenModeRef.current = mode;
    initialStateModel.setInitialState(
      (s: Record<string, unknown> | undefined) => ({
        ...(s || {}),
        themeMode: mode,
      })
    );
  }, [mode, initialStateModel]);

  const setPreference = useCallback(
    (next: ThemePreference) => setPreferenceState(next),
    []
  );
  const setMode = useCallback(
    (next: ThemeMode) => setPreferenceState(next),
    []
  );
  const toggle = useCallback(() => {
    setPreferenceState((prev) => {
      const current = prev === "system" ? getSystemMode() : prev;
      return current === "dark" ? "light" : "dark";
    });
  }, []);

  const ctxValue = useMemo(
    () => ({ mode, preference, setPreference, setMode, toggle }),
    [mode, preference, setPreference, setMode, toggle]
  );

  return (
    <ThemeContext.Provider value={ctxValue}>
      {/* wave disabled 关掉 antd 默认点击波纹特效 */}
      <ConfigProvider
        renderEmpty={renderUnifiedEmpty}
        wave={{ disabled: true }}
        {...ANTD_ICON_CONFIG}
      >
        {/* 内层再包一层 antd <App>:umi antd 插件默认把 <App> 包在外层 ConfigProvider
            (没 token),会让 useToast 拿到无主题的 message 实例(toast 白底)。 */}
        <ThemeScope mode={mode}>
          <App message={TOAST_MESSAGE_CONFIG} style={{ colorScheme: mode }}>
            <ToastBridge />
            {children}
          </App>
        </ThemeScope>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeRoot;
